import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import type { LobbyView } from '@mafia/core';
import { emit } from '@/lib/socket';
import { errorText } from '@/lib/format';
import { useGame } from '@/store/game';
import { Alert, Button, Card, Field, Input, PageSpinner } from '@/components/ui';
import { Lobby } from '@/game/Lobby';
import { GameScreen } from '@/game/GameScreen';

export function RoomPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const { lobby, view, viewRoomId, connection } = useGame();
  const [needPassword, setNeedPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const attempted = useRef<string | null>(null);

  const join = useCallback(
    async (password?: string) => {
      setJoining(true);
      const res = await emit<LobbyView>('room:join', { roomId: id, password }, { silent: true });
      setJoining(false);
      if (res.ok) {
        setNeedPassword(false);
        setError(null);
        if (res.data) useGame.setState({ lobby: res.data });
      } else if (res.error === 'WRONG_PASSWORD') {
        setNeedPassword(true);
        setError(password ? 'Неверный пароль' : null);
      } else {
        setError(errorText(res.error, res.message));
      }
    },
    [id],
  );

  // Вход в комнату при открытии страницы (и повторно — после переподключения).
  useEffect(() => {
    if (connection !== 'online' || lobby?.roomId === id) return;
    if (attempted.current === id && (error || needPassword)) return;
    attempted.current = id;
    const password = (location.state as { password?: string } | null)?.password;
    void join(password);
  }, [connection, id, lobby?.roomId, join, location.state, error, needPassword]);

  const inThisRoom = lobby?.roomId === id;
  const gameVisible =
    view && viewRoomId === id && (view.phase === 'GAME_OVER' || (inThisRoom && view.gameId === lobby.gameId));

  if (gameVisible) return <GameScreen view={view} lobby={inThisRoom ? lobby : null} />;
  if (inThisRoom) {
    // Игра уже идёт, а снапшот ещё не пришёл.
    if (lobby.gameId) return <PageSpinner />;
    return <Lobby lobby={lobby} />;
  }
  if (needPassword) return <PasswordGate error={error} pending={joining} onSubmit={join} />;
  if (error) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card className="space-y-4 p-6 text-center">
          <p className="text-4xl">🚪</p>
          <p>{error}</p>
          <Link to="/">
            <Button variant="secondary">К списку комнат</Button>
          </Link>
        </Card>
      </div>
    );
  }
  return <PageSpinner />;
}

function PasswordGate({ error, pending, onSubmit }: { error: string | null; pending: boolean; onSubmit(password: string): void }) {
  const [password, setPassword] = useState('');
  return (
    <div className="mx-auto max-w-sm px-4 py-16">
      <Card className="p-6">
        <form
          className="space-y-4"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            onSubmit(password);
          }}
        >
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-gold-400" />
            <h1 className="font-display text-xl">Приватная комната</h1>
          </div>
          {error && <Alert>{error}</Alert>}
          <Field label="Пароль">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
          </Field>
          <Button type="submit" className="w-full" loading={pending} disabled={!password}>
            Войти в комнату
          </Button>
        </form>
      </Card>
    </div>
  );
}
